import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, Truck, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useShop } from "../lib/store";
import { Price, Stars } from "./Common";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, useFetch } from "../lib/api";

const SWATCH = { oak: "#c59b6c", walnut: "#6b4630", white: "#f5f4f0", black: "#1c1c1e", sage: "#8a9a82", sand: "#d8cbb5", anthracite: "#3c3d3f", grey: "#9b9b98", cognac: "#9a5b2e", natural: "#e0c9a6" };
export const Swatch = ({ color, size = 12 }) => <span className="inline-block rounded-full border border-black/10" style={{ width: size, height: size, background: SWATCH[color] || "#ccc" }} />;

export const WishButton = ({ slug, className = "" }) => {
  const { inWish, toggleWish } = useShop();
  const { t } = useI18n();
  const on = inWish(slug);
  return (
    <button
      aria-label={t("wishlist")} aria-pressed={on} data-testid={`wishlist-toggle-${slug}`}
      onClick={(e) => { e.preventDefault(); toggleWish(slug); toast.success(on ? t("removed_wish") : t("added_wish")); }}
      className={`w-9 h-9 grid place-items-center bg-white/95 border border-[color:var(--hw-line)] rounded-full hover:border-[color:var(--hw-ink)] transition-colors ${className}`}
    >
      <Heart size={16} className={on ? "fill-[color:var(--hw-sale)] text-[color:var(--hw-sale)]" : ""} />
    </button>
  );
};

export const ProductCard = ({ p, index = 0 }) => {
  const { t, val } = useI18n();
  const { toggleCompare, compare } = useShop();
  const [quick, setQuick] = useState(false);
  return (
    <article className="group relative fade-up" style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }} data-testid={`product-card-${p.slug}`}>
      <Link to={`/p/${p.slug}`} className="block" data-testid={`product-card-link-${p.slug}`}>
        <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--hw-beige)]">
          <img src={p.image} alt={p.name} loading="lazy" width="480" height="600" className="absolute inset-0 w-full h-full object-cover img-zoom" />
          {p.image2 && <img src={p.image2} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-0 md:group-hover:opacity-100 transition-opacity duration-500" />}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {p.discount_pct > 0 && <span className="badge-sale" data-testid="product-card-sale-badge">−{p.discount_pct}%</span>}
          </div>
        </div>
      </Link>
      <div className="absolute top-3 right-3 flex flex-col gap-2">
        <WishButton slug={p.slug} />
        <button aria-label={t("quick_view")} data-testid={`quick-view-${p.slug}`} onClick={() => setQuick(true)} className="w-9 h-9 grid place-items-center bg-white/95 border border-[color:var(--hw-line)] rounded-full hover:border-[color:var(--hw-ink)]"><Eye size={16} /></button>
        <button aria-label={t("compare_add")} aria-pressed={compare.includes(p.slug)} data-testid={`compare-toggle-${p.slug}`}
          onClick={() => { if (!toggleCompare(p.slug)) toast.error(t("compare_full")); }}
          className={`w-9 h-9 grid place-items-center border rounded-full ${compare.includes(p.slug) ? "bg-[color:var(--hw-green)] text-white border-transparent" : "bg-white/95 border-[color:var(--hw-line)]"}`}><GitCompare size={15} /></button>
      </div>
      <div className="pt-4 space-y-1.5">
        <p className="eyebrow !tracking-[0.12em]">{p.brand}</p>
        <Link to={`/p/${p.slug}`}><h3 className="font-sans text-[15px] font-medium leading-snug hover:underline">{p.name}</h3></Link>
        <Price price={p.price} compare={p.compare_at} from={p.from_price} testid={`product-card-price-${p.slug}`} />
        <Stars value={p.rating_avg} count={p.review_count} />
        <div className="flex items-center gap-2 text-xs text-[color:var(--hw-muted)]">
          {p.colors?.length > 0 && <span className="flex gap-1" aria-label={p.colors.map((c) => val("c", c)).join(", ")}>{p.colors.map((c) => <Swatch key={c} color={c} />)}</span>}
          {p.sizes?.length > 1 && <span>{t("sizes_available", { n: p.sizes.length })}</span>}
        </div>
        <p className={`text-xs flex items-center gap-1.5 ${p.in_stock ? "text-[color:var(--hw-green)]" : "text-[color:var(--hw-sale)]"}`}>
          {p.in_stock ? <><Truck size={13} /> {t("delivery_in", { a: p.delivery_days?.[0], b: p.delivery_days?.[1] })}</> : t("out_of_stock")}
        </p>
      </div>
      {quick && <QuickView slug={p.slug} onClose={() => setQuick(false)} />}
    </article>
  );
};

const QuickView = ({ slug, onClose }) => {
  const { t, lang, val } = useI18n();
  const { addToCart } = useShop();
  const { data } = useFetch(`/products/${slug}?lang=${lang}`);
  const [sku, setSku] = useState(null);
  const v = data && (data.variants.find((x) => x.sku === sku) || data.variants.find((x) => x.stock > 0) || data.variants[0]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none p-0 overflow-hidden" data-testid="quick-view-modal">
        {!data ? <div className="p-10">{t("loading")}</div> : (
          <div className="grid md:grid-cols-2">
            <img src={v.image || data.images[0]} alt={data.name} className="w-full h-full object-cover aspect-[4/5]" />
            <div className="p-6 space-y-4">
              <DialogTitle className="font-serif text-3xl">{data.name}</DialogTitle>
              <DialogDescription className="text-sm text-[color:var(--hw-muted)] line-clamp-4">{data.description}</DialogDescription>
              <Price price={v.price} compare={v.compare_at} size="lg" />
              <select className="field" value={v.sku} onChange={(e) => setSku(e.target.value)} data-testid="quick-view-variant-select" aria-label={t("size")}>
                {data.variants.map((x) => <option key={x.sku} value={x.sku} disabled={x.stock < 1}>{[x.size && val("s", x.size), x.color && val("c", x.color)].filter(Boolean).join(" · ")} – {x.stock > 0 ? t("in_stock") : t("out_of_stock")}</option>)}
              </select>
              <button className="btn-primary w-full" disabled={v.stock < 1} data-testid="quick-view-add-to-cart"
                onClick={() => { addToCart(v.sku, 1, slug); toast.success(t("added_cart")); onClose(); }}>{t("add_to_cart")}</button>
              <Link to={`/p/${slug}`} className="block text-center text-sm link-u" onClick={onClose}>{t("view_product")}</Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ProductGrid = ({ items, cols = "lg:grid-cols-4" }) => (
  <div className={`grid grid-cols-2 md:grid-cols-3 ${cols} gap-x-4 gap-y-10 lg:gap-x-6`} data-testid="product-grid">
    {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
  </div>
);

export const ProductRail = ({ items }) => (
  <div className="flex gap-4 lg:gap-6 overflow-x-auto no-scrollbar snap-x pb-2" data-testid="product-rail">
    {items.map((p, i) => <div key={p.id} className="snap-start shrink-0 w-[62%] sm:w-[38%] lg:w-[23%]"><ProductCard p={p} index={i} /></div>)}
  </div>
);
